import { Type } from "@sinclair/typebox";
import { ActionButtonNames } from "@whatslly/types/ActionButtonNames";
import { AppsNames } from "./AppsNames";
import { ThreadMessageType } from "@whatslly/types/ThreadMessageQueueItem";
import { UserPlatform } from "@whatslly/types/UserPlatform";
import { TypeBoxToType } from "@whatslly/types/TypeBoxToType";
import { LayoutField } from "@whatslly/types/LayoutFieldType";
import { LoginProvider } from "./LoginProviderType";
import { OrgConfigs } from "./OrgConfigs";

const ActionsOptions = Type.Record(ActionButtonNames, Type.Union([Type.Literal("show"), Type.Literal("hide")]));

const IntegrationsOptions = Type.Record(AppsNames, Type.Optional(Type.Union([Type.Literal("show"), Type.Literal("hide")])));

const UiV2Options = Type.Record(Type.Union([Type.Literal("FilerOpenWapTasks")]), Type.Union([Type.Literal("show"), Type.Literal("hide")]));

export type ActionsOptionsType = TypeBoxToType<typeof ActionsOptions>;
export type IntegrationsOptionsType = TypeBoxToType<typeof IntegrationsOptions>;
export type UiV2OptionsType = TypeBoxToType<typeof UiV2Options>;

const InstanceUploadFileConfig = Type.Object({
  maxFileSizeBytes: Type.Optional(
    Type.Number({
      description: "max allow file upload size in kb",
    })
  ),
  allowMessageTypes: Type.Optional(Type.Array(ThreadMessageType)),
  autoSyncMediaMessageMimeTypes: Type.Optional(Type.Array(Type.String())),
  ignoreInvalidMediaFiles: Type.Optional(Type.Boolean()),
});

export type InstanceUploadFileConfigType = TypeBoxToType<typeof InstanceUploadFileConfig>;

const CreateCustomObject = Type.Record(
  Type.String(),
  Type.Object({
    apiName: Type.String(),
    text: Type.String(),
    personTypes: Type.Array(Type.String()),
    leadFieldName: Type.Optional(Type.String()),
    contactFieldName: Type.Optional(Type.String()),
    accountFieldName: Type.Optional(Type.String()),
    personAccountFieldName: Type.Optional(Type.String()),
    defaultLayout: Type.Array(LayoutField),
    iconName: Type.Optional(Type.String()),
    iconStyle: Type.Optional(Type.Record(Type.String(), Type.String())),
    extraFields: Type.Optional(Type.Record(Type.String(), Type.String())),
  })
);

export type CreateCustomObjectType = TypeBoxToType<typeof CreateCustomObject>;

const ShowCustomObjectModalActionFieldSection = Type.Object({
  objectApiName: Type.Optional(Type.String()),
  checkboxText: Type.String(),
  predefinedFields: Type.Optional(Type.Record(Type.String(), Type.Union([Type.String(), Type.Number(), Type.Boolean()]))),
  fields: Type.Array(LayoutField),
});

export type ShowCustomObjectModalActionFieldSectionType = TypeBoxToType<typeof ShowCustomObjectModalActionFieldSection>;

const ShowCustomObjectModalActionFields = Type.Array(Type.Union([LayoutField, ShowCustomObjectModalActionFieldSection]));

export type ShowCustomObjectModalActionFieldsType = TypeBoxToType<typeof ShowCustomObjectModalActionFields>;

const ShowCustomObjectModalAction = Type.Object({
  type: Type.Literal("modal"),
  fields: ShowCustomObjectModalActionFields,
  predefinedFields: Type.Optional(Type.Record(Type.String(), Type.Union([Type.String(), Type.Number(), Type.Boolean()]))),
  iconName: Type.String(),
  buttonText: Type.String(),
  submitButtonText: Type.Optional(Type.String()),
  cancelButtonText: Type.Optional(Type.String()),
  modalTitle: Type.String(),
  reloadDataAfterSubmit: Type.Optional(Type.Boolean()),
  hideRules: Type.Optional(Type.Record(Type.String(), Type.Array(Type.Union([Type.String(), Type.Number(), Type.Boolean()])))),
  id: Type.Optional(Type.String()),
});

const ShowCustomObject = Type.Record(
  Type.String(),
  Type.Object({
    apiName: Type.String(),
    text: Type.String(),
    personTypes: Type.Array(Type.String()),
    iconName: Type.Optional(Type.String()),
    iconStyle: Type.Optional(Type.Record(Type.String(), Type.String())),
    pageSettings: Type.Object({
      header: Type.String(),
      content: Type.Optional(Type.String()),
      upperCustomDataObject: Type.Optional(Type.Array(LayoutField)),
    }),
    querySettings: Type.Object({
      entityIdField: Type.String(),
      predefinedFields: Type.Optional(Type.Record(Type.String(), Type.String())),
      orderBy: Type.Optional(Type.Array(Type.String())),
    }),
    defaultLayout: Type.Array(LayoutField),
    actions: Type.Optional(
      Type.Array(
        Type.Union([
          Type.Object({
            id: Type.Optional(Type.String()),
            type: Type.Literal("view-in-crm"),
            hideRules: Type.Optional(Type.Record(Type.String(), Type.Array(Type.Union([Type.String(), Type.Number(), Type.Boolean()])))),
            fields: Type.Optional(Type.Array(Type.Any())),
          }),
          ShowCustomObjectModalAction,
        ])
      )
    ),
  })
);

export type ShowCustomObjectType = TypeBoxToType<typeof ShowCustomObject>;

export const UserSupportedLanguage = Type.Union([Type.Literal("SPANISH"), Type.Literal("PORTUGUESE"), Type.Literal("ENGLISH")]);

export type UserSupportedLanguageType = TypeBoxToType<typeof UserSupportedLanguage>;

export const LayoutFieldsSettings = Type.Object({
  showContacts: Type.Boolean(),
  showLeads: Type.Boolean(),
  allowEditLayoutDragAndDrop: Type.Optional(Type.Boolean()),
  showActivities: Type.Boolean(),
  showTasks: Type.Boolean(),
  showCases: Type.Boolean(),
  showOpps: Type.Boolean(),
  showCalendar: Type.Optional(Type.Boolean()),
  showPersonAccount: Type.Optional(Type.Boolean()),
  showCustomObject: Type.Optional(ShowCustomObject),
  createLeads: Type.Boolean(),
  createContacts: Type.Boolean(),
  createPersonAccount: Type.Optional(Type.Boolean()),
  createActivities: Type.Boolean(),
  createTasks: Type.Boolean(),
  createCases: Type.Boolean(),
  actions: ActionsOptions,
  createCustomObject: Type.Optional(CreateCustomObject),
  layoutSettingsLimits: Type.Optional(Type.Record(Type.String(), Type.Number())),
  showRecordTypeLayout: Type.Optional(Type.Boolean()),
  showWhatsllyTaskIcon: Type.Boolean(),
  subtypes: Type.Optional(Type.Record(Type.String(), Type.String())),
  showEditLayouts: Type.Boolean(),
  createBusinessAccount: Type.Optional(Type.Boolean()),
  editLead: Type.Optional(Type.Boolean()),
  editContact: Type.Optional(Type.Boolean()),
  editPersonAccount: Type.Optional(Type.Boolean()),
  editActivity: Type.Optional(Type.Boolean()),
  editTask: Type.Optional(Type.Boolean()),
  editCase: Type.Optional(Type.Boolean()),
  editBusinessAccount: Type.Optional(Type.Boolean()),
  editOpportunity: Type.Optional(Type.Boolean()),
});

export type LayoutFieldsSettingsType = TypeBoxToType<typeof LayoutFieldsSettings>;

export const UserChannels = Type.Object({
  waba: Type.Optional(Type.Array(Type.String())),
  twilio: Type.Optional(
    Type.Array(
      Type.Object({
        phoneSid: Type.String(),
        allowedCapabilities: Type.Optional(
          Type.Object({
            sms: Type.Boolean(),
            voice: Type.Boolean(),
          })
        ),
      })
    )
  ),
  sidecar: Type.Optional(Type.Array(Type.String())),
  "whatsapp-web": Type.Optional(Type.Object({ enabled: Type.Boolean() })),
  telegram: Type.Optional(Type.Object({ enabled: Type.Boolean() })),
  imessage: Type.Optional(Type.Object({ enabled: Type.Boolean() })),
  "mobile-call-recording": Type.Optional(Type.Object({ enabled: Type.Boolean() })),
});
export type UserChannels = TypeBoxToType<typeof UserChannels>;

export const UserDefaultChannels = Type.Object({
  "whatsapp-web": Type.Optional(Type.Boolean()),
  telegram: Type.Optional(Type.Boolean()),
  imessage: Type.Optional(Type.Boolean()),
  "mobile-call-recording": Type.Optional(Type.Boolean()),
});

export type UserDefaultChannels = TypeBoxToType<typeof UserDefaultChannels>;

export const UserAccountPermission = Type.Object({
  orgId: Type.String(),
  permission: Type.String(),
});

export const UserAccount = Type.Object({
  accountId: Type.String(),
  isAccountAdmin: Type.Boolean(),
  permissions: Type.Array(UserAccountPermission),
});

export const User = Type.Intersect([
  Type.Object({
    _id: Type.String(),
    name: Type.String(),
    loginProvider: Type.Optional(LoginProvider),
    freeUser: Type.Boolean(),
    platformUser: Type.Boolean(),
    showContactOpps: Type.Optional(Type.Boolean()),
    logoUrl: Type.Optional(Type.String()),
    timezoneOffset: Type.Optional(Type.Number()),
    base: Type.Optional(Type.String()),
    organizationId: Type.Optional(Type.String()),
    orgIds: Type.Optional(Type.Array(Type.String())),
    userId: Type.Optional(Type.String()),
    groupIds: Type.Optional(Type.Array(Type.String())),
    email: Type.Optional(Type.String()),
    phone: Type.Optional(Type.String()),
    phones: Type.Optional(Type.Array(Type.String())),
    username: Type.Optional(Type.String()),
    firstName: Type.Optional(Type.String()),
    lastName: Type.Optional(Type.String()),
    apex: Type.Boolean(),
    newUser: Type.Optional(Type.Boolean()),
    createdAt: Type.Optional(Type.Number()),
    trialDays: Type.Optional(Type.Number()),
    accounts: Type.Optional(Type.Array(UserAccount)),
    isPartner: Type.Optional(Type.Boolean()),
    showUploadFiles: Type.Boolean(),
    showAutoSync: Type.Boolean(),
    showTemplates: Type.Boolean(),
    templates: Type.Optional(
      Type.Object({
        edit: Type.Boolean(),
        add: Type.Boolean(),
      })
    ),
    showOppsStatusBar: Type.Boolean(),
    showLeadStatusBar: Type.Boolean(),
    showUserAssignNewCase: Type.Boolean(),
    showUserAssignNewTask: Type.Boolean(),
    showUserAssignNewActivities: Type.Boolean(),
    showAccountBox: Type.Boolean(),
    specialActivitiesTabName: Type.Optional(Type.String()),
    integrations: IntegrationsOptions,
    UiV2: UiV2Options,
    phoneField: Type.Optional(
      Type.Object({
        newLead: Type.String(),
        newContact: Type.String(),
        newLeadPhonePrefix: Type.Optional(Type.String()),
        newContactPhonePrefix: Type.Optional(Type.String()),
      })
    ),
    taskDescField: Type.Optional(Type.String()),
    accountNameField: Type.Optional(Type.String()),
    firstNameField: Type.Optional(Type.String()),
    lastNameField: Type.Optional(Type.String()),
    allowToSyncFiles: Type.Optional(Type.Boolean()),
    allowToSyncVcard: Type.Optional(Type.Boolean()),
    uploadFile: Type.Optional(InstanceUploadFileConfig),
    userPlatform: Type.Optional(UserPlatform),
    customText: Type.Optional(
      Type.Object({
        contact: Type.Optional(Type.String()),
        lead: Type.Optional(Type.String()),
        scheduleEvent: Type.Optional(Type.String()),
        event: Type.Optional(Type.String()),
        case: Type.Optional(Type.String()),
        task: Type.Optional(Type.String()),
        activity: Type.Optional(Type.String()),
        create: Type.Optional(Type.String()),
        caseTab: Type.Optional(Type.String()),
        taskTab: Type.Optional(Type.String()),
        activityTab: Type.Optional(Type.String()),
        personAccount: Type.Optional(Type.String()),
        showOppsTooltip: Type.Optional(Type.String()),
        showCasesTooltip: Type.Optional(Type.String()),
        showTasksTooltip: Type.Optional(Type.String()),
        showActivitiesTooltip: Type.Optional(Type.String()),
        createActivities: Type.Optional(Type.String()),
        createTasks: Type.Optional(Type.String()),
        editActivities: Type.Optional(Type.String()),
        editTasks: Type.Optional(Type.String()),
        oppsPageTitle: Type.Optional(Type.String()),
        personTasksPageTitle: Type.Optional(Type.String()),
        accountTasksPageTitle: Type.Optional(Type.String()),
        personActivitiesPageTitle: Type.Optional(Type.String()),
        accountActivitiesPageTitle: Type.Optional(Type.String()),
      })
    ),
    showCalendarVideo: Type.Optional(Type.Boolean()),
    showGrantLoginAccess: Type.Optional(Type.Boolean()),
    showGlobalSearch: Type.Optional(Type.Boolean()),
    hideWhatsAppFilters: Type.Optional(Type.Boolean()),
    showDefaultRecord: Type.Optional(Type.Boolean()),
    showAppDirectory: Type.Optional(Type.Boolean()),
    createdBefore: Type.Number(),
    eliteSecurityToken: Type.Optional(Type.String()),
    eliteSecurityTokenUserAgentHeader: Type.Optional(Type.Boolean()),
    desktopLimitNumberOfChannels: Type.Optional(Type.Number()),
    hideCrmLinks: Type.Optional(Type.Boolean()),
    selectiveSync: Type.Union([Type.Literal("hide"), Type.Literal("enabled"), Type.Literal("disabled"), Type.Literal("enable_without_auto")]),
    showWhatsllyTask: Type.Boolean(),
    showBlurMode: Type.Boolean(),
    optionsInWidget: Type.Optional(Type.Boolean()),
    trackAnalytics: Type.Boolean(),
    trackMessageTemplateId: Type.Boolean(),
    showEmailAttachment: Type.Boolean(),
    filterByCountryCode: Type.Boolean(),
    filterByBusinessLabels: Type.Boolean(),
    hideCreateBusinessAccount: Type.Optional(Type.Boolean()),
    searchBusinessAccountNameField: Type.Optional(Type.String()),
    extraSearchAccountNameField: Type.Optional(Type.String()),
    allowChangeLeadStatus: Type.Optional(Type.Boolean()),
    allowChangeOppStage: Type.Optional(Type.Boolean()),
    showEmailOpportunityField: Type.Optional(Type.Boolean()),
    showEventOpportunityField: Type.Optional(Type.Boolean()),
    hideMarkTaskAsCompleted: Type.Optional(Type.Boolean()),
    showActionsMenuSubmitIdea: Type.Optional(Type.Boolean()),
    showUnInstalledApps: Type.Optional(Type.Boolean()),
    dataBoxSendToEmailPage: Type.Optional(Type.Boolean()),
    showEtokenExpiredModal: Type.Optional(Type.Boolean()),
    showOutlookSendToEmailPage: Type.Optional(Type.Boolean()),
    allowSyncGroups: Type.Optional(Type.Boolean()),
    show_chat_groups_ui: Type.Optional(Type.Boolean()),
    show_im_groups_ui: Type.Optional(Type.Boolean()),
    isAdmin: Type.Optional(Type.Boolean()),
    isSuperAdmin: Type.Optional(Type.Boolean()),
    isSupportUser: Type.Optional(Type.Boolean()),
    disablePhoneFieldMagicLogic: Type.Optional(Type.Boolean()),
    showNewEditLayoutEtities: Type.Optional(Type.Boolean()),
    blockBrowserExtension: Type.Optional(Type.Boolean()),
    showFavoriteTemplates: Type.Optional(Type.Boolean()),
    blockUnknownPerson: Type.Optional(Type.Boolean()),
    allowBlockedPhones: Type.Optional(Type.Boolean()),
    blurContactOnBlockUnknownPerson: Type.Optional(Type.Boolean()),
    blockGroupsWithUnknownContacts: Type.Optional(Type.Boolean()),
    blockChangeProfileImage: Type.Optional(Type.Boolean()),
    enableDependentPicklist: Type.Optional(Type.Boolean()),
    limitSFopenUrl: Type.Optional(Type.Boolean()),
    limitOpenExternalUrl: Type.Optional(Type.Boolean()),
    limitLoginViaSfMobilePhone: Type.Optional(Type.Boolean()),
    allowedMobilePhone: Type.Optional(Type.Array(Type.String())),
    sidecars_enabled: Type.Optional(Type.Boolean()),
    show_analytics_ui: Type.Optional(Type.Boolean()),
    show_dashboards_ui: Type.Optional(Type.Boolean()),
    show_custom_filters_ui: Type.Optional(Type.Boolean()),
    allow_dlp: Type.Optional(Type.Boolean()),
    enable_surveillance: Type.Optional(Type.Boolean()),
    dlp_audio: Type.Optional(Type.Boolean()),
    allow_waba: Type.Optional(Type.Boolean()),
    allow_twilio: Type.Optional(Type.Boolean()),
    allow_imessage: Type.Optional(Type.Boolean()),
    allow_telegram: Type.Optional(Type.Boolean()),
    new_agent_mode: Type.Optional(Type.Boolean()),
    allow_inviting_users: Type.Optional(Type.Boolean()),
    onlyDesktopLiteVersion: Type.Optional(Type.Boolean()),
    customErrorFailedToQuery: Type.Optional(Type.String()),
    language: Type.Optional(UserSupportedLanguage),
    showChatNow: Type.Optional(Type.Boolean()),
    openChatNowModalOnUnknownPhoneNumber: Type.Optional(Type.Boolean()),
    createCaseOppField: Type.Optional(Type.String()),
    disableOpportunitiesDefaultLayoutFieldForEntities: Type.Optional(
      Type.Array(Type.Union([Type.Literal("task"), Type.Literal("activity"), Type.Literal("case")]))
    ),
    useNewMessageSynchronization: Type.Optional(Type.Boolean()),
    autoSyncMessageUpdates: Type.Optional(Type.Boolean()),
    allow_desktop_lite: Type.Optional(Type.Boolean()),
    allow_mobile_call_recording: Type.Optional(Type.Boolean()),
    block_whatsapp_calls: Type.Optional(Type.Boolean()),
    deleteCRMSessionOnLogoutOnDesktop: Type.Optional(Type.Boolean()),
    allow_platform_integrations: Type.Optional(Type.Boolean()),
    setWhatIdAccountIdNewTaskAndActivity: Type.Optional(Type.Boolean()),
    emitAckChanges: Type.Optional(Type.Boolean()),
    defaultNotificationsOverDueOnly: Type.Optional(Type.Boolean()),
    prefixPhoneNumberToRemove: Type.Optional(Type.Array(Type.String())),
    desktopForceNewTab: Type.Optional(Type.Boolean()),
    allowCreateSupportCase: Type.Optional(Type.Boolean()),
    allow_platform_global_layout: Type.Optional(Type.Boolean()),
    allow_platform_admin_notifications: Type.Optional(Type.Boolean()),
    access_audit_log: Type.Optional(Type.Boolean()),
    show_saml_settings: Type.Optional(Type.Boolean()),
    disableRetroactiveSync: Type.Optional(Type.Boolean()),
    syncronizeLastDays: Type.Optional(Type.Number()),
    allowEditEntities: Type.Object({
      lead: Type.Optional(Type.Boolean()),
      contact: Type.Optional(Type.Boolean()),
      personAccount: Type.Optional(Type.Boolean()),
      case: Type.Optional(Type.Boolean()),
      task: Type.Optional(Type.Boolean()),
      activity: Type.Optional(Type.Boolean()),
      opportunity: Type.Optional(Type.Boolean()),
      account: Type.Optional(Type.Boolean()),
      businessAccount: Type.Optional(Type.Boolean()),
    }),
    periodicAutoSync: Type.Optional(Type.Boolean()),
    periodicAutoSyncInterval: Type.Optional(Type.Number()),
    periodicGroupSync: Type.Optional(Type.Boolean()),
    periodicGroupSyncInterval: Type.Optional(Type.Number()),
    allowLinkUnknowPerson: Type.Optional(Type.Boolean()),
    enableRecordTypesPickList: Type.Optional(Type.Boolean()),
    enableCreateEntitiesToNotFocusedContact: Type.Optional(Type.Boolean()),
    reportToMultipleDestinations: Type.Optional(Type.Boolean()),
    profileId: Type.Optional(Type.String()),
    profileName: Type.Optional(Type.String()),
    filterAnalyticsByProfile: Type.Optional(Type.Boolean()),
    wPod: Type.String(),
    autoSyncDefault: Type.Boolean(),
    autoSyncGroupsDefault: Type.Boolean(),
    returnSelectiveToPrev: Type.Optional(Type.Boolean()),
    createBusinessAccount: Type.Optional(Type.Boolean()),
    permissions: Type.String(),
    scrollToEarliestMessage: Type.Optional(Type.Boolean()),
    showAddToCampaign: Type.Optional(
      Type.Object({
        Lead: Type.Boolean(),
        Contact: Type.Boolean(),
        Account: Type.Boolean(),
        PersonAccount: Type.Boolean(),
      })
    ),
    disableAccountRequireOnContact: Type.Optional(Type.Boolean()),
    showCreateAccountUnderCreateContact: Type.Optional(Type.Boolean()),
    hideCreateAccountOptionUnderCreateContact: Type.Optional(Type.Boolean()),
    showCreateAccountUnderEditContact: Type.Optional(Type.Boolean()),
    use_only_wap_special_link: Type.Optional(Type.Boolean()),
    query_cache_ttl: Type.Optional(Type.Number()),
    hide_widget_close_button: Type.Optional(Type.Boolean()),
    hide_forward_button: Type.Optional(Type.Boolean()),
    allow_embedded_signup_with_partner: Type.Optional(Type.Boolean()),
    hide_whatsapp_channel: Type.Optional(Type.Boolean()),
    hide_telegram_channel: Type.Optional(Type.Boolean()),
    hide_wap_channels_button_in_desktop: Type.Optional(Type.Boolean()),
    show_only_wap_channels_in_desktop: Type.Optional(Type.Boolean()),
    hide_meta_ai_button_in_desktop: Type.Optional(Type.Boolean()),
    default_favorite_actions: Type.Optional(Type.Array(Type.String())),
    hide_intro_video: Type.Optional(Type.Boolean()),
    channels: Type.Optional(UserChannels),
    managerId: Type.Optional(Type.String()),
    allow_messages_analytics: Type.Optional(Type.Boolean()),
    allow_customer_facing_settings: Type.Optional(Type.Boolean()),
    allow_public_api: Type.Optional(Type.Boolean()),
    allow_url_policy: Type.Optional(Type.Boolean()),
    force_user_phone: Type.Optional(Type.Boolean()),
    desktop_add_paired_phone_on_login: Type.Optional(Type.Boolean()),
    max_paired_phones_on_login: Type.Optional(Type.Number()),
    replace_phone_on_limit_exceeded: Type.Optional(Type.Boolean()),
    enable_limit_number_of_contacts_engaged: Type.Optional(Type.Boolean()),
    limit_number_of_contacts_engaged: Type.Optional(Type.Number()),
    enable_limit_number_of_msgs_to_unresponsive_contact: Type.Optional(Type.Boolean()),
    limit_number_of_msgs_to_unresponsive_contact: Type.Optional(Type.Number()),
    hide_edit_user: Type.Optional(Type.Boolean()),
    hide_logout: Type.Optional(Type.Boolean()),
    fraud_detection: Type.Optional(Type.Boolean()),
    phishing_fraud_detection: Type.Optional(Type.Boolean()),
    phones_blocked_from_sync: Type.Optional(Type.Array(Type.String())),
    watermark: Type.Optional(Type.Boolean()),
    protect_downloads_with_password: Type.Optional(Type.String()),
    hide_block_contact_button: Type.Optional(Type.Boolean()),
    hide_delete_conversation_button: Type.Optional(Type.Boolean()),
    allow_keyword_insights: Type.Optional(Type.Boolean()),
    analytics_compliance: Type.Optional(Type.Boolean()),
    allow_phones_whitelist: Type.Optional(Type.Boolean()),
    allow_phones_blocklist: Type.Optional(Type.Boolean()),
    allow_blocked_phones: Type.Optional(Type.Boolean()),
    allow_tuvis_contacts: Type.Optional(Type.Boolean()),
    disable_save_as_at_desktop: Type.Optional(Type.Boolean()),
    auto_sync_to_duplicate_records: Type.Optional(Type.Boolean()),
    /** @deprecated Retained only so pre-EN-6640 desktop/browser-ext builds keep gating group-participant scraping on this flag — their release cycle lags server deploys. Current clients collect unconditionally and reporting is gated at the reporter layer; remove once the desktop/ext minimum versions are past this release. */
    collect_messages_group_participants: Type.Optional(Type.Boolean()),
    query_cache_ttl_nightly: Type.Optional(Type.Number()),
    allow_whatsapp_channels: Type.Optional(Type.Boolean()),
    collect_whatsapp_status_messages: Type.Optional(Type.Boolean()),
    incidents_filter_matched_keyword_campaigns_default_value: Type.Optional(Type.Boolean()),
    block_copy_paste: Type.Optional(Type.Boolean()),
    restrict_to_direct_reports: Type.Optional(Type.Boolean()),
    view_only_own_messages: Type.Optional(Type.Boolean()),
    salesforce_use_browser: Type.Optional(Type.Boolean()),
    recordTypeNames: Type.Optional(Type.Record(Type.String(), Type.Record(Type.String(), Type.String()))),
    multipleRecordsExtraFields: Type.Optional(Type.Array(Type.String())),
    allow_slack_platform: Type.Optional(Type.Boolean()),
    allow_teams_platform: Type.Optional(Type.Boolean()),
    allow_archive: Type.Optional(Type.Boolean()),
    allow_teams_archive: Type.Optional(Type.Boolean()),
    allow_bot_builder: Type.Optional(Type.Boolean()),
    allow_elite_token: Type.Optional(Type.Boolean()),
    allow_im_templates: Type.Optional(Type.Boolean()),
    track_filter_analytics: Type.Optional(Type.Boolean()),
    sync_whatsapp_voice_video: Type.Optional(Type.Boolean()),
    enable_whatsapp_voice_video: Type.Optional(Type.Boolean()),
    desktopVersion: Type.Optional(Type.String()),
  }),
  LayoutFieldsSettings,
]);

export type UserType = TypeBoxToType<typeof User>;
export type UserWithOrgInstanceConfigs = UserType & { permissionsInstanceConfigs?: OrgConfigs };
